import { useEffect, useRef, useState, useCallback } from "react";
import { Room, RoomEvent, Track, createLocalTracks } from "livekit-client";
import api from "@/lib/api";
import guestApi from "@/lib/guestApi";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

/**
 * Cloudivo Meet — Enterprise mode powered by LiveKit Cloud's SFU.
 *
 * Media (camera/mic/screen-share) flows through a LiveKit `Room`. App-level
 * features that have nothing to do with media transport — chat, raise-hand,
 * waiting-room admission, breakout-room events — reuse the existing
 * lightweight WebSocket relay (`meeting_utils.py`), so those UI panels and
 * their persisted history don't need a second real-time channel.
 *
 * When `guestToken` is provided (an unauthenticated attendee who registered
 * and was approved), REST calls go through `guestApi` (Bearer header instead
 * of a session cookie) and the WS URL carries `?guest_token=` instead of
 * relying on cookies — see `auth_utils.get_current_user_or_guest[_ws]`.
 */
export default function useLiveKitMeeting(meetingId, meetingTypeHint, isHostHint, guestToken) {
  const [phase, setPhase] = useState("connecting"); // connecting | connected | ended | error | waiting_approval | denied
  const [selfInfo, setSelfInfo] = useState(null);
  const [peers, setPeers] = useState({}); // identity -> { name, is_host, videoTrack, audioTrack, screenTrack, audio, video }
  const [micOn, setMicOn] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [raisedHands, setRaisedHands] = useState({});
  const [waitingRequests, setWaitingRequests] = useState([]);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localScreenTrack, setLocalScreenTrack] = useState(null);
  const [breakoutRooms, setBreakoutRooms] = useState([]);
  const [breakoutSignal, setBreakoutSignal] = useState(null); // { type: "closed" | "assigned", breakoutId? } — bumped fresh on every server push

  const httpClient = guestToken ? guestApi : api;
  const wsRef = useRef(null);
  const roomRef = useRef(null);
  const selfInfoRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const genRef = useRef(0); // bumped once per mount-effect run — detects a stale/superseded
  // effect invocation (e.g. React.StrictMode's dev-only double-invoke) for the WS connection's
  // isStale() check and the effect's own cleanup. Deliberately NOT touched by switchRoom() —
  // see roomGenRef below for why this must be a separate concern.
  const roomGenRef = useRef(0); // bumped every time the LiveKit `Room` object itself changes
  // (initial connect AND every switchRoom() call) — used only by attachRoomMediaHandlers'
  // Disconnected handler and switchRoom's own stale-check, to tell a superseded/old Room's
  // events apart from the currently active one. This used to incorrectly share `genRef` with
  // the WS-staleness concern above, which meant the WS handler silently stopped processing
  // ALL messages (chat, breakout events, presence) for any participant after their first
  // room switch — see CHANGELOG for the regression this caused.
  const expectingTeardownRef = useRef(false); // set the instant a breakout close/reassignment WS
  // message arrives, so the LiveKit-forced disconnect that the server's room deletion triggers
  // (which can race ahead of switchRoom() bumping roomGenRef) isn't misread as a real meeting end.

  const sendJson = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(msg));
  }, []);

  const upsertPeer = useCallback((identity, patch) => {
    setPeers((prev) => ({ ...prev, [identity]: { ...(prev[identity] || {}), ...patch } }));
  }, []);

  const attachRoomMediaHandlers = useCallback((room, myRoomGen) => {
    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      const identity = participant.identity;
      if (track.kind === Track.Kind.Video && publication.source === Track.Source.ScreenShare) {
        upsertPeer(identity, { screenTrack: track });
      } else if (track.kind === Track.Kind.Video) {
        upsertPeer(identity, { videoTrack: track, video: true });
      } else if (track.kind === Track.Kind.Audio) {
        upsertPeer(identity, { audioTrack: track, audio: true });
      }
    });
    room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      const identity = participant.identity;
      if (track.kind === Track.Kind.Video && publication.source === Track.Source.ScreenShare) {
        upsertPeer(identity, { screenTrack: null });
      } else if (track.kind === Track.Kind.Video) {
        upsertPeer(identity, { videoTrack: null, video: false });
      } else if (track.kind === Track.Kind.Audio) {
        upsertPeer(identity, { audioTrack: null, audio: false });
      }
    });
    room.on(RoomEvent.TrackMuted, (publication, participant) => {
      if (publication.kind === Track.Kind.Video) upsertPeer(participant.identity, { video: false });
      if (publication.kind === Track.Kind.Audio) upsertPeer(participant.identity, { audio: false });
    });
    room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
      if (publication.kind === Track.Kind.Video) upsertPeer(participant.identity, { video: true });
      if (publication.kind === Track.Kind.Audio) upsertPeer(participant.identity, { audio: true });
    });
    room.on(RoomEvent.LocalTrackPublished, (publication) => {
      if (publication.source === Track.Source.ScreenShare && publication.track) {
        setLocalScreenTrack(publication.track);
      }
    });
    room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
      if (publication.source === Track.Source.ScreenShare) {
        setLocalScreenTrack(null);
      }
    });
    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      setPeers((prev) => { const next = { ...prev }; delete next[participant.identity]; return next; });
    });
    room.on(RoomEvent.ParticipantConnected, (participant) => {
      // Seed the tile's display name straight from LiveKit's own participant metadata
      // (set at token generation via `.withName()`) the instant they join the SFU room —
      // instead of only via the separate WS chat/presence channel's `peer_joined` message,
      // which can arrive a moment later than the LiveKit media/track events and briefly
      // render a nameless "?" avatar tile in the meantime.
      upsertPeer(participant.identity, { name: participant.name });
    });
    room.on(RoomEvent.Disconnected, () => {
      // Only the Room generation that's still current is allowed to end the meeting UI —
      // a stale/superseded connection (e.g. the duplicate created by React.StrictMode's
      // dev-only double-invoke, which LiveKit Cloud force-disconnects as a duplicate
      // identity) must NOT be able to falsely flip a still-live session to "ended".
      if (roomGenRef.current !== myRoomGen) return;
      if (expectingTeardownRef.current) {
        // A breakout close/reassignment WS message already arrived (or the server's
        // room-deletion push beat it here) — this disconnect is that server-forced
        // side-effect, NOT a real meeting end. switchRoom() (triggered by the room
        // component's effect reacting to the WS signal) is about to bump roomGenRef and
        // reconnect; give it a brief moment to do so before deciding this is real.
        setTimeout(() => {
          if (roomGenRef.current === myRoomGen) setPhase((p) => (p === "connected" ? "ended" : p));
        }, 1200);
        return;
      }
      setPhase((p) => (p === "connected" ? "ended" : p));
    });
  }, [upsertPeer]);

  const connectToRoom = useCallback(async (meetIdArg, myRoomGen) => {
    const { data } = await httpClient.post(`/meetings/${meetIdArg}/livekit/token`);
    const room = new Room({ adaptiveStream: true, dynacast: true });
    attachRoomMediaHandlers(room, myRoomGen);
    await room.connect(data.server_url, data.token);
    // Seed names for anyone already in the room before we joined — `ParticipantConnected`
    // only fires for participants who join AFTER us, not for ones already present.
    room.remoteParticipants.forEach((participant) => {
      upsertPeer(participant.identity, { name: participant.name });
    });
    return room;
  }, [attachRoomMediaHandlers, upsertPeer, httpClient]);

  useEffect(() => {
    const myGen = ++genRef.current;
    const myRoomGen = ++roomGenRef.current;
    expectingTeardownRef.current = false;
    const isStale = () => genRef.current !== myGen;
    let cancelled = false; // per-effect-run closure flag (NOT a ref/shared value) — this is what
    // actually distinguishes "this specific run was torn down" from "some other run exists",
    // and is checked BEFORE any network call so a React.StrictMode phantom double-invoke never
    // gets far enough to open a real LiveKit connection in the first place (see below).

    const setupChatSocket = () => {
      const wsUrl = guestToken
        ? `${WS_BASE}/api/meetings/ws/${meetingId}?guest_token=${encodeURIComponent(guestToken)}`
        : `${WS_BASE}/api/meetings/ws/${meetingId}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        if (isStale()) return;
        const msg = JSON.parse(evt.data);
        if (msg.type === "existing_peers") {
          selfInfoRef.current = msg.self;
          setSelfInfo(msg.self);
          const initial = {};
          msg.peers.forEach((p) => { initial[p.user_id] = { name: p.name, is_host: p.is_host }; });
          setPeers((prev) => ({ ...initial, ...prev }));
        } else if (msg.type === "peer_joined") {
          upsertPeer(msg.user_id, { name: msg.name, is_host: msg.is_host });
        } else if (msg.type === "peer_left") {
          setPeers((prev) => { const next = { ...prev }; delete next[msg.user_id]; return next; });
          setRaisedHands((prev) => { const next = { ...prev }; delete next[msg.user_id]; return next; });
        } else if (msg.type === "chat_message") {
          setChatMessages((prev) => [...prev, msg]);
        } else if (msg.type === "raise_hand") {
          setRaisedHands((prev) => {
            const next = { ...prev };
            if (msg.raised) next[msg.user_id] = msg.name; else delete next[msg.user_id];
            return next;
          });
        } else if (msg.type === "meeting_ended") {
          setPhase("ended");
        } else if (msg.type === "waiting_for_approval") {
          setPhase("waiting_approval");
        } else if (msg.type === "waiting_room_denied") {
          setPhase("denied");
        } else if (msg.type === "waiting_room_request") {
          setWaitingRequests((prev) => (prev.some((r) => r.user_id === msg.user_id) ? prev : [...prev, { user_id: msg.user_id, name: msg.name }]));
        } else if (msg.type === "waiting_room_left") {
          setWaitingRequests((prev) => prev.filter((r) => r.user_id !== msg.user_id));
        } else if (msg.type === "breakout_rooms_updated") {
          setBreakoutRooms(msg.breakout_rooms || []);
        } else if (msg.type === "breakout_rooms_closed") {
          expectingTeardownRef.current = true;
          setBreakoutRooms([]);
          setBreakoutSignal({ type: "closed", ts: Date.now() });
        } else if (msg.type === "breakout_assigned") {
          expectingTeardownRef.current = true;
          setBreakoutSignal({ type: "assigned", breakoutId: msg.breakout_id, title: msg.title, ts: Date.now() });
        }
      };
    };

    const setup = async () => {
      // Yield one microtask first. React 18 StrictMode's dev-only double-invoke runs
      // mount -> cleanup -> mount SYNCHRONOUSLY within the same tick (before any promise
      // continuation), so if this run is the phantom first invocation, its cleanup below
      // will already have set `cancelled = true` by the time we resume here — letting us
      // bail out WITHOUT EVER calling connectToRoom()/room.connect(). This matters because
      // a post-hoc room.disconnect() on an already-connected duplicate room is NOT reliable:
      // LiveKit-client's own internal ICE-retry/reconnect logic can still race and briefly
      // re-establish a zombie duplicate-identity connection, which then gets force-kicked by
      // LiveKit Cloud's server-side dedup — but that kick can land on the CORRECT/current
      // connection instead of the stale one, falsely ending a perfectly live session.
      await Promise.resolve();
      if (cancelled) return;
      try {
        const room = await connectToRoom(meetingId, myRoomGen);
        if (cancelled || isStale()) { room.disconnect(); return; }
        roomRef.current = room;
        setupChatSocket();
        setPhase("connected");

        const wantVideo = meetingTypeHint !== "class" || !!isHostHint;
        try {
          const tracks = await createLocalTracks({ audio: true, video: wantVideo });
          if (cancelled || isStale()) { tracks.forEach((t) => t.stop()); room.disconnect(); return; }
          for (const track of tracks) {
            if (track.kind === Track.Kind.Video) {
              localVideoTrackRef.current = track;
              setLocalVideoTrack(track);
              await room.localParticipant.publishTrack(track);
              setCamOn(true);
            } else {
              localAudioTrackRef.current = track;
              setLocalAudioTrack(track);
              await room.localParticipant.publishTrack(track);
              setMicOn(true);
            }
          }
        } catch { /* camera/mic permission denied — user can retry via controls */ }
      } catch (e) {
        if (!cancelled && !isStale()) setPhase("error");
      }
    };
    setup();

    return () => {
      cancelled = true;
      // Only the winning/surviving generation ever actually acquired roomRef/track refs/ws
      // (the phantom run bails out above before touching any of them), so this is safe.
      if (genRef.current === myGen) { // eslint-disable-line react-hooks/exhaustive-deps
        roomRef.current?.disconnect();
        roomRef.current = null;
        localVideoTrackRef.current?.stop();
        localAudioTrackRef.current?.stop();
        localVideoTrackRef.current = null;
        localAudioTrackRef.current = null;
        wsRef.current?.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !micOn;
    await room.localParticipant.setMicrophoneEnabled(next);
    setMicOn(next);
    sendJson({ type: "media_state", audio: next, video: camOn });
  }, [micOn, camOn, sendJson]);

  const toggleCam = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    if (meetingTypeHint === "class" && !selfInfoRef.current?.is_host) return;
    const next = !camOn;
    await room.localParticipant.setCameraEnabled(next);
    setCamOn(next);
    sendJson({ type: "media_state", audio: micOn, video: next });
  }, [camOn, micOn, meetingTypeHint, sendJson]);

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      const next = !screenSharing;
      await room.localParticipant.setScreenShareEnabled(next);
      setScreenSharing(next);
    } catch { /* user cancelled the share picker */ }
  }, [screenSharing]);

  const sendChat = useCallback((text) => sendJson({ type: "chat_message", text }), [sendJson]);
  const toggleRaiseHand = useCallback((raised) => sendJson({ type: "raise_hand", raised }), [sendJson]);
  const admitParticipant = useCallback((userId) => {
    sendJson({ type: "admit_participant", user_id: userId });
    setWaitingRequests((prev) => prev.filter((r) => r.user_id !== userId));
  }, [sendJson]);
  const denyParticipant = useCallback((userId) => {
    sendJson({ type: "deny_participant", user_id: userId });
    setWaitingRequests((prev) => prev.filter((r) => r.user_id !== userId));
  }, [sendJson]);

  /** Switches the local participant into a breakout room (or back to the main
   * room when `breakoutId` is null), tearing down and re-establishing the
   * LiveKit connection with a freshly scoped token. Bumps the generation
   * token so the original mount effect's cleanup/stale-checks stay consistent
   * with whichever room is now the "current" one. */
  const switchRoom = useCallback(async (breakoutId) => {
    expectingTeardownRef.current = false; // this generation change is intentional/expected — the
    // Disconnected handler's grace-timeout for the OLD room's myRoomGen will now correctly no-op
    // since roomGenRef is bumped below before that timeout ever fires.
    const myRoomGen = ++roomGenRef.current;
    const prevVideo = localVideoTrackRef.current, prevAudio = localAudioTrackRef.current;
    roomRef.current?.disconnect();
    roomRef.current = null;
    setPeers({});
    setPhase("connecting");
    try {
      const endpoint = breakoutId ? `/meetings/${meetingId}/breakout-rooms/${breakoutId}/token` : `/meetings/${meetingId}/livekit/token`;
      const { data } = await httpClient.post(endpoint);
      const newRoom = new Room({ adaptiveStream: true, dynacast: true });
      attachRoomMediaHandlers(newRoom, myRoomGen);
      await newRoom.connect(data.server_url, data.token);
      if (roomGenRef.current !== myRoomGen) { newRoom.disconnect(); return; }
      roomRef.current = newRoom;
      if (prevVideo) await newRoom.localParticipant.publishTrack(prevVideo);
      if (prevAudio) await newRoom.localParticipant.publishTrack(prevAudio);
      setPhase("connected");
    } catch {
      setPhase("error");
    }
  }, [meetingId, attachRoomMediaHandlers, httpClient]);

  return {
    phase, selfInfo, peers, micOn, camOn, screenSharing, chatMessages, raisedHands, waitingRequests,
    localVideoTrack, localAudioTrack, localScreenTrack, toggleMic, toggleCam, toggleScreenShare, sendChat, toggleRaiseHand,
    admitParticipant, denyParticipant, switchRoom, room: roomRef.current, breakoutRooms, breakoutSignal,
  };
}
